import React from 'react';
import FeatherIcon from '../components/FeatherIcon';
import { ThemePreference, useTheme } from './ThemeProvider';

interface Option {
  readonly value: ThemePreference;
  readonly icon: string;
  readonly label: string;
}

const OPTIONS: readonly Option[] = [
  { value: 'light', icon: 'sun', label: 'Light' },
  { value: 'auto', icon: 'monitor', label: 'Match browser' },
  { value: 'dark', icon: 'moon', label: 'Dark' }
];

interface Props {
  /** Classes for the wrapping group, so each placement keeps its own layout. */
  readonly className?: string;
  /** Show the option labels beside the icons — used in the mobile menu, where there is room. */
  readonly showLabels?: boolean;
}

/**
 * Three-state theme control: light, match-browser, dark. A radio group rather than a
 * two-state switch because "auto" is a real choice, not the absence of one — a visitor
 * whose OS flips at sunset should be able to ask for that explicitly.
 */
const ThemeToggle: React.FC<Props> = ({ className = '', showLabels = false }) => {
  const { preference, setPreference } = useTheme();

  // The track sits a step *back* from the page surface in both themes, so the raised active
  // pill (bg-card) reads as selected without leaning on a shadow — which all but disappears
  // against a dark ground.
  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={`inline-flex items-center gap-0.5 rounded-full border border-line bg-paper p-0.5 ${className}`}
    >
      {OPTIONS.map(({ value, icon, label }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setPreference(value)}
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              active
                ? 'bg-card text-primary shadow-sm ring-1 ring-line'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <FeatherIcon name={icon} size={16} />
            {showLabels && <span className="font-medium">{label}</span>}
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
